import {html, css, LitElement} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {classMap} from 'lit/directives/class-map.js';
import {connect} from 'pwa-helpers';
import {when} from 'lit/directives/when.js';
import store from '../../store';
import {selectIomoColorTheme, selectTestMaterial1, selectTestMaterial2, selectUseTestMaterial} from '../iomoSelectors';
import {setTestMaterial1, setTestMaterial2, setUseTestMaterial} from '../iomoSlice';
import {inputRange} from '../../css/styleSets';


@customElement('physical-material')
export class PhysicalMaterial extends connect(store)(LitElement) {


	static styles = [
		inputRange,
		inputRange,
		css`
			.physicalMaterial {
				break-inside: avoid;
				padding: 10px;
				width: 400px;
				color: var(--color-white);
				font: var(--font-widget-label);
				position: absolute;
				right: 150px;
				bottom: 0;
				background-color: rgba(50, 50, 50, 0.3);
			}
			.tools {
				display: flex;
				justify-content: space-between;
				align-items: center;
			}
			.material-properties {
				display: flex;
				flex-direction: row;
				gap: 20px;
				div {
					flex-basis: 50%;
				}
			}
			.title {
				display: flex;
				justify-content: space-between;
				align-items: center;
				font: var(--font-widget-title);
				color: var(--color-white);
				margin-top: 8px;
				margin-bottom: 3px;
			}

			.description {
				align-self: start;
				color: var(--color-white);
				display: flex;
				height: 100%;
				border-left: 1px solid var(--color-widget-border);
				flex-direction: column;
				font-family: var(--font-family-light), sans-serif;
				font-size: var(--font-size-widget-small-mono);
				@media (max-width: 768px) {
					margin-top: calc(2 * var(--sections-gap));
				}
			}

			.description-text {
				margin-top: auto;
			}

			.description-text p {
				margin-bottom: 0;
			}

			.icon {
				width: 11px;
				height: 11px;
				padding: 5px;
				display: inline-block;
			}

			.icon svg {
				display: block;
				fill: currentColor
			}

			.icon.satisfied {
				color: var(--color-white);
			}

			.debug > div {
				flex-basis: 33%;
			}

			.select {
				font: var(--font-widget-title);
				padding: 5px;
				border: 0;
				background-color: transparent;
				color: var(--color-white);
			}
			.copy {
				font: var(--font-widget-title);
				padding: 5px;
				border: 0;
				background-color: transparent;
				color: var(--color-white);
				cursor: pointer;
			}

			input[type=range] {
				position: absolute;
			}

			.range {
				position: relative;
				width: 100%;
				padding: 18px 0;
			}

			.track {
				position: absolute;
				width: 100%;
				height: 1px;
				top: 18px;
				background-color: var(--color-white);
			}

		`];

	@property({type: String})
	materialKind = '';

	@property({type: Object})
	material = {
		color: '#999999',
		emissive: '#000000',

		roughness: 0, // 0-1
		metalness: 0, // 0-1

		clearcoat: 0, // 0-1
		clearcoatRoughness: 0, // 0-1
		ior: 1.5, // 1.0 - 2.333
		iridescence: 0, // 0-1
		iridescenceIOR: 1.3, // 1.0 - 2.333
		reflectivity: 0, // 0-1

		sheen: 0, // 0-1
		sheenRoughness: 1, // 0-1
		sheenColor: '#000000',

		specularIntensity: 1, // 0-1
		specularColor: '#ffffff',

		normalScale: 0,
		clearcoatNormalScale: 0
	};

	constructor() {
		super();
		this.materialKind = 'default';
		store.subscribe(() => this.requestUpdate());
	}

	connectedCallback() {
		super.connectedCallback();
	}

	stateChanged(state) {
		this.state = state;
		this.lang = state.status.lang;
		this.debug = state.status.debug;
		this.colorTheme = selectIomoColorTheme(state);

		this.useTestMaterial = selectUseTestMaterial(state);
		if (this.materialKind === 'material1') {
			this.material = selectTestMaterial1(state);
		} else {
			this.material = selectTestMaterial2(state);
		}
		//console.log('physical material state', this.useTestMaterial, this.materialKind, this.material);
	}

	setProperty(prop, value) {
		const material = Object.assign({}, this.material);
		material[prop] = value;
		this.material = material;
		this.dispatchMaterial();
	}

	setMaterialKind(event) {
		this.materialKind = event.target.value;
		store.dispatch(
			setUseTestMaterial({useTestMaterial: this.materialKind !== 'default'})
		);
	}
	dispatchMaterial() {
		if (this.materialKind === 'material1') {
			store.dispatch(
				setTestMaterial1({material: this.material})
			);
		} else {
			store.dispatch(
				setTestMaterial2({material: this.material})
			);
		}
	}

	copyMaterialsToClipboard() {
		const materials = {
			material1: selectTestMaterial1(this.state),
			material2: selectTestMaterial2(this.state),
		};

		navigator.clipboard.writeText(JSON.stringify(materials, null, 2)).catch((err) => {
			console.error('Could not copy materials: ', err);
		});
	}

	render() {

		return html`
			<div class="physicalMaterial">
				<div class="tools">
					<select class='select' name="material" @change="${this.setMaterialKind}">
						<option value="default">Default</option>
						<option value="material1">Material 1</option>
						<option value="material2">Material 2</option>
					</select>
					<button class="copy" type="button" @click="${this.copyMaterialsToClipboard}">Copy materials</button>
				</div>
				${when(
					this.materialKind !== 'default',
					() => html`
						<div class="material-properties">
							<div>
								<div class="title">Color = ${this.material.color}</div>
								<input type="color" .value=${this.material.color}
								       @change="${(e) => this.setProperty('color', e.target.value)}">

								<div class="title">Roughness = ${this.material.roughness}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="0" max="1" step="0.01"
									       .value=${this.material.roughness} @input="${(e) => this.setProperty('roughness', e.target.value)}">
								</div>
								<div class="title">Metalness = ${this.material.metalness}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="0" max="1" step="0.01"
									       .value=${this.material.metalness} @input="${(e) => this.setProperty('metalness', e.target.value)}">
								</div>

								<div class="title">Reflectivity = ${this.material.reflectivity}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="0" max="1" step="0.01"
									       .value=${this.material.reflectivity} @input="${(e) => this.setProperty('reflectivity', e.target.value)}">
								</div>


								<div class="title">Specular Intensity = ${this.material.specularIntensity}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="0" max="1" step="0.01"
									       .value=${this.material.specularIntensity} @input="${(e) => this.setProperty('specularIntensity', e.target.value)}">
								</div>

								<div class="title">Specular Color = ${this.material.specularColor}</div>
								<input type="color" .value=${this.material.specularColor}
								       @change="${(e) => this.setProperty('specularColor', e.target.value)}">

								<div class="title">Clearcoat = ${this.material.clearcoat}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="0" max="1" step="0.01"
									       .value=${this.material.clearcoat} @input="${(e) => this.setProperty('clearcoat', e.target.value)}">
								</div>

								<div class="title">Clearcoat Roughness = ${this.material.clearcoatRoughness}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="0" max="1" step="0.01"
									       .value=${this.material.clearcoatRoughness} @input="${(e) => this.setProperty('clearcoatRoughness', e.target.value)}">
								</div>

								<div class="title">Emissive = ${this.material.emissive}</div>
								<input type="color" .value=${this.material.emissive}
								       @change="${(e) => this.setProperty('emissive', e.target.value)}">
							</div>
							<div>
								<div class="title">Sheen = ${this.material.sheen}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="0" max="1" step="0.01"
									       .value=${this.material.sheen} @input="${(e) => this.setProperty('sheen', e.target.value)}">
								</div>

								<div class="title">Sheen Roughness = ${this.material.sheenRoughness}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="0" max="1" step="0.01"
									       .value=${this.material.sheenRoughness} @input="${(e) => this.setProperty('sheenRoughness', e.target.value)}">
								</div>

								<div class="title">Sheen Color = ${this.material.sheenColor}</div>
								<div class="range">
									<input type="color" .value=${this.material.sheenColor}
									       @change="${(e) => this.setProperty('sheenColor', e.target.value)}">
								</div>

								<div class="title">IOR = ${this.material.ior}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="1" max="2.333" step="0.01"
									       .value=${this.material.ior} @input="${(e) => this.setProperty('ior', e.target.value)}">
								</div>

								<div class="title">Iridescence = ${this.material.iridescence}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="0" max="1" step="0.01"
									       .value=${this.material.iridescence} @input="${(e) => this.setProperty('iridescence', e.target.value)}">
								</div>

								<div class="title">Iridescence IOR = ${this.material.iridescenceIOR}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="1" max="2.333" step="0.01"
									       .value=${this.material.iridescenceIOR} @input="${(e) => this.setProperty('iridescenceIOR', e.target.value)}">
								</div>

								<div class="title">NormalScale = ${this.material.normalScale}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="-5" max="5" step="0.01"
									       .value=${this.material.normalScale} @input="${(e) => this.setProperty('normalScale', e.target.value)}">
								</div>

								<div class="title">ClearcoatNormalScale = ${this.material.clearcoatNormalScale}</div>
								<div class="range">
									<div class="track"></div>
									<input class="input" type="range" min="-5" max="5" step="0.01"
									       .value=${this.material.clearcoatNormalScale} @input="${(e) => this.setProperty('clearcoatNormalScale', e.target.value)}">
								</div>
							</div>
						</div>
					`)}
		`;
	}
}
